/**
 * PCM / Opus 语音：MQTT 主题、播放环缓与 .env（文件名历史保留为 Codec2Constants）。
 * 上行路径由下行接收主题决定（H2）：`downstream` → `upstream`，`downstream/opus` → `upstream/opus`。
 */

function envTrim(key: string): string {
  return (process.env[key] || '').trim();
}

function envTruthy(value: string): boolean {
  const v = value.toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/** 下行语音格式（.env 回退，无主题信息时使用） */
export type VoiceDownstreamMode = 'pcm' | 'opus';

/** MQTT 下行音频种类 */
export type MqttAudioDownstreamKind = 'pcm' | 'opus';

export function getMqttAudioDownstreamKindFromTopic(topic: string): MqttAudioDownstreamKind {
  if (topic.includes('/downstream/opus')) {
    return 'opus';
  }
  return 'pcm';
}

/** PCM：audio/device/{id}/downstream */
export function getMqttAudioDownstreamPcmTopic(deviceId: string): string {
  return `audio/device/${deviceId}/downstream`;
}

/** Opus：audio/device/{id}/downstream/opus */
export function getMqttAudioDownstreamOpusTopic(deviceId: string): string {
  return `audio/device/${deviceId}/downstream/opus`;
}

/** PCM上行：audio/user/{id}/upstream */
export function getMqttAudioUpstreamUserTopic(deviceId: string): string {
  return `audio/user/${deviceId}/upstream`;
}

/** Opus 上行：audio/user/{id}/upstream/opus */
export function getMqttAudioUpstreamOpusUserTopic(deviceId: string): string {
  return `audio/user/${deviceId}/upstream/opus`;
}

/**
 * @deprecated 请使用 getMqttAudioDownstreamPcmTopic；双主题订阅见 MqttAudioManager.subscribeToDeviceAudio
 */
export function getMqttAudioDownstreamTopic(deviceId: string): string {
  return getMqttAudioDownstreamPcmTopic(deviceId);
}

export function getVoiceDownstreamMode(): VoiceDownstreamMode {
  const raw = envTrim('REACT_APP_VOICE_DOWNSTREAM');
  if (raw.toLowerCase() === 'opus') {
    return 'opus';
  }
  return 'pcm';
}

/** 是否走 Opus 下行（WebCodecs 解码；无主题信息时的 .env 回退） */
export function isVoiceDownstreamOpus(): boolean {
  return getVoiceDownstreamMode() === 'opus';
}

/** WebCodecs Opus 上行：48 kHz，20 ms/帧 */
export const OPUS_UPLINK_SAMPLE_RATE = 48000;
/** 20 ms @ 48 kHz */
export const OPUS_UPLINK_FRAME_SAMPLES = 960;
/** 默认 48 kbps */
export const OPUS_UPLINK_BITRATE_BPS = 48000;

/**
 * Opus 上行：送入 WebCodecs 编码器前的线性增益（1 = 不变）。
 * Worklet 侧 Int16 前会对峰值做约 0.95 的软限幅，整体可能偏“小”；可适当 >1 补偿。
 * .env: `REACT_APP_UPLINK_OPUS_GAIN`（例如 1.5、2）；过大易硬削波，建议先 1.25～1.75 试听。
 */
export function getOpusUplinkInputGain(): number {
  const raw = envTrim('REACT_APP_UPLINK_OPUS_GAIN');
  if (!raw) {
    return 1;
  }
  const n = parseFloat(raw);
  if (!Number.isFinite(n) || n <= 0) {
    return 1;
  }
  return Math.min(Math.max(n, 0.25), 4);
}

/**
 * AudioWorklet 环形缓冲区容量（交错 float，双声道 LRLR…）
 * .env: REACT_APP_AUDIO_RING_BUFFER_FLOATS，默认 65536（约 682ms @ 48k 立体声）
 */
export function getAudioPlaybackRingBufferFloats(): number {
  const raw = envTrim('REACT_APP_AUDIO_RING_BUFFER_FLOATS');
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 4096) {
    return Math.min(n, 524288);
  }
  return 65536;
}

/**
 * 开始稳定播放前最少积压在环缓里的交错样本数（48k 双声道约每 ms 96 个 float）
 * .env: REACT_APP_AUDIO_MIN_BUFFER_SAMPLES，默认 4800（约 50ms）
 */
export function getAudioPlaybackMinInterleavedSamples(): number {
  const raw = envTrim('REACT_APP_AUDIO_MIN_BUFFER_SAMPLES');
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 0) {
    return Math.min(n, 192000);
  }
  return 4800;
}

/** .env: REACT_APP_AUDIO_AGGRESSIVE_RING_START=1 时任意非空缓冲即开声（更低延迟、更易欠载爆音） */
export function isAudioPlaybackAggressiveRingStart(): boolean {
  return envTruthy(envTrim('REACT_APP_AUDIO_AGGRESSIVE_RING_START'));
}

/**
 * 用户组实时下行：Worklet 环缓超过该毫秒数则清空旧音频、只追最新（默认 300ms）。
 * .env: REACT_APP_VOICE_GROUP_MAX_PLAYBACK_BACKLOG_MS
 */
export function getVoiceGroupMaxPlaybackBacklogMs(): number {
  const raw = envTrim('REACT_APP_VOICE_GROUP_MAX_PLAYBACK_BACKLOG_MS');
  const n = parseInt(raw || '', 10);
  if (Number.isFinite(n) && n >= 50 && n <= 3000) {
    return n;
  }
  return 300;
}
